/**
 * Phone-call deployment settings — `Deployment.deploymentSettings` when type is
 * TWILIO_VOICE.
 */

import { z } from "zod";

import { SupportedTwilioRegionSchema } from "../../../../api/domains/deployments/twilio-region.types";
import { DeploymentAdvancedSettingsSchema } from "./DeploymentAdvancedSettings.schema";
import { DeploymentAssistantSettingsSchema } from "./DeploymentAssistantSettings.schema";
import { GeminiLiveModelSettingSchema } from "./GeminiLiveModels.schema";
import { GeminiThinkingLevelChosenAtSchema } from "./GeminiLiveThinkingLevelChoice.schema";
import { GeminiCustomVocabularySchema } from "./GeminiLiveTranscription.schema";
import { GEMINI_LIVE_THINKING_LEVELS } from "./GeminiLiveTunables.schema";
import { GeminiLiveTurnDetectionSchema } from "./GeminiLiveTurnDetection.schema";
import { GptLiveDelegationModelSchema, GptLiveNativeModeSchema } from "./GptLive.schema";
import { GreetingPlaybackSettingSchema } from "./GreetingPlayback.schema";
import {
  OpenAIRealtimeFeatureSettingsSchema,
  OpenAIRealtimeModelIdSchema
} from "./OpenAIRealtimeModels.schema";
import { OpenAIRealtimeTurnDetectionSchema } from "./OpenAIRealtimeTurnDetection.schema";
import { PhoneCallFlowSchema } from "./PhoneCallFlow.schema";
import { PhoneCallSendSmsSchema } from "./PhoneCallSendSms.schema";
import { PhoneCallSessionTimeoutSchema } from "./PhoneCallSessionTimeout.schema";
import { PhoneCallSpeechToTextSchema } from "./PhoneCallSpeechToText.schema";
import { PhoneCallTransferSchema } from "./PhoneCallTransfer.schema";
import { PhoneCallUpfrontActionSchema } from "./PhoneCallUpfrontAction.schema";
import { PhoneNumberPurchasedSchema } from "./PhoneNumberPurchased.schema";
import { TtsProviderSchema } from "./TtsProviders.schema";
import { VOICE_PROVIDERS } from "./VoiceProviders.schema";

/**
 * Twilio Voice Deployment Settings Schema
 * Used for TWILIO_VOICE deployment type
 */
export const PhoneCallDeploymentSettingsSchema = z.object({
  // Twilio credentials (legacy - now usually in ApiKeyConnection)
  accountSid: z.string().optional(),
  authToken: z.string().optional(),

  // Phone number configuration
  phoneNumber: z.string(),
  phonePrice: z.string(),
  phoneRegion: z.string(),

  // Initial greeting
  initialMessage: z.string().optional(),
  initialMessageUrl: z.string().optional(),
  // How that greeting reaches the caller. Absent on every row written before it
  // existed; those resolve to "live" — see GreetingPlaybackSettingSchema.
  greetingPlayback: GreetingPlaybackSettingSchema,

  // Purchased phone details
  purchasedPhoneNumber: PhoneNumberPurchasedSchema.optional(),

  // Assistant configuration
  assistantSettings: DeploymentAssistantSettingsSchema.optional(),
  advancedSettings: DeploymentAdvancedSettingsSchema.optional(),

  // Voice configuration
  language: z.string(),
  // Voice AI provider for the realtime session. Legacy "millis" values in the DB
  // are normalized to undefined by .catch() — no data migration needed.
  // undefined = "openai" (backward compat for deployments created before multi-provider).
  provider: z.enum(VOICE_PROVIDERS).optional().catch(undefined),
  // Free-form model label kept for backward compat (legacy rows store either an
  // OpenAI ID or a Gemini ID here). New code reads `openaiModel` for OpenAI
  // sessions and `geminiModel` for Gemini sessions.
  model: z.string().optional(),
  // Selected OpenAI Realtime model. Validates against the registry; unknown
  // values from legacy rows are normalized to undefined (adapter falls back
  // to the registry default).
  openaiModel: OpenAIRealtimeModelIdSchema.optional().catch(undefined),
  // Per-deployment OpenAI feature toggles (e.g. preambles). Capability-gated
  // at runtime — flags for unsupported models are silently ignored.
  openaiSettings: OpenAIRealtimeFeatureSettingsSchema.optional(),
  // Selected Gemini Live model. Absent on every row written before it existed;
  // those resolve to 3.1 with no backfill — see GeminiLiveModelSettingSchema.
  geminiModel: GeminiLiveModelSettingSchema,
  ttsProvider: TtsProviderSchema,
  voiceId: z.string(),
  // Gemini thinking level — controls reasoning depth vs latency tradeoff.
  // Only used when provider = "gemini". Sent only beside
  // `geminiThinkingLevelChosenAt` — see readGeminiThinkingLevelChoice.
  geminiThinkingLevel: z.enum(GEMINI_LIVE_THINKING_LEVELS).optional(),
  // When the level above was picked through a surface that sends it. A level
  // stored without it predates the send, and stays unsent.
  geminiThinkingLevelChosenAt: GeminiThinkingLevelChosenAtSchema,
  // Phrases Gemini's input transcription is biased toward. Only used when
  // provider = "gemini". Absent = no bias.
  geminiCustomVocabulary: GeminiCustomVocabularySchema,
  // The Responses-API model a GPT-Live session delegates tool calls and
  // reasoning to. Required when provider = "gpt-live", enforced on write.
  gptLiveDelegationModel: GptLiveDelegationModelSchema,
  // Speech-to-speech instead of the Responses backend. undefined = off.
  gptLiveNativeMode: GptLiveNativeModeSchema,

  // Region configuration — validated against operational Twilio regions
  hostRegion: SupportedTwilioRegionSchema,

  // Call handling
  transfers: z.array(PhoneCallTransferSchema),
  sendSms: z.array(PhoneCallSendSmsSchema),
  keywords: z.array(z.string()).optional(),

  // Speech configuration
  // NOTE: the following three fields use snake_case because they were
  // defined by the legacy Millis AI API and are stored that way in the DB.
  // Renaming requires a JSON data migration — tracked for Phase 2.
  vad_threshold: z.number(),
  switch_languages: z.array(z.string()),
  speech_to_text: PhoneCallSpeechToTextSchema.optional(),

  // Turn detection — provider-specific. Each adapter consumes its native shape.
  //
  // Exactly one of these two fields is consulted at runtime, picked by `provider`:
  //   provider === "openai" → openaiTurnDetection
  //   provider === "gemini" → geminiTurnDetection
  //
  // Both are optional so legacy rows that escaped the migration still parse;
  // adapters fall back to DEFAULT_*_TURN_DETECTION when missing.
  openaiTurnDetection: OpenAIRealtimeTurnDetectionSchema.optional(),
  geminiTurnDetection: GeminiLiveTurnDetectionSchema.optional(),

  // Session configuration
  // NOTE: snake_case — same reason as above.
  session_timeout: PhoneCallSessionTimeoutSchema,
  flow: PhoneCallFlowSchema,

  // Upfront actions (different structure for phone calls)
  upfrontActions: PhoneCallUpfrontActionSchema.optional(),

  // Whitelist and SMS
  whitelist: z.array(z.string()),
  enableSms: z.boolean()
});

export type PhoneCallDeploymentSettings = z.infer<typeof PhoneCallDeploymentSettingsSchema>;
